/* eslint-disable react-hooks/exhaustive-deps */
import { getAddressFromServer } from 'helpers/googlePlacesHelper';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import React, { JSX } from 'react';
import LocationInput, { LocationInputHandle } from '../site-structure/Header/LocationInput';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import { getAddress } from '../content/LocationHeader';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { tv } from 'tailwind-variants';
import { Field, LinkField, useSitecore } from '@sitecore-content-sdk/nextjs';
import { Data } from '.generated/Foundation.CW.model';
import { withStandardComponentWrapper } from 'helpers/HOC';
import classNames from 'classnames';
import { ComponentProps } from 'lib/component-props';
import { useRouter } from 'next/router';
import { useSiteSettings } from 'lib/utils/page-data-static-props/pageDataContext';
import { useAppointmentModal } from 'helpers/Context/AppointmentModalProvider';

export interface Location {
  address: string;
  lat: number | null;
  lng: number | null;
}

type LocationProps = Data.BaseTemplateSections.Location & {
  url?: string;
  id?: string;
  distance?: number;
  distanceDisplay?: string;
  fields?: {
    Title?: Field<string>;
    state?: Data.Lookups.Lookup;
    PageType?: {
      fields?: {
        Value: Field<string>;
      };
    };
  };
};

type LocationListingProps = ComponentProps & {
  params: {
    hideSearchBarOnLocationCount: string;
  };
};

const LocationListing = (props: LocationListingProps): JSX.Element => {
  const { getDictionaryValue } = useDictionary();
  const router = useRouter();

  const locationRef = React.useRef<LocationInputHandle>(null);
  const [location, setLocation] = React.useState<Location | null>(null);
  const { route } = useSitecore().page?.layout?.sitecore;
  const [locationsList, setLocationsList] = React.useState<Array<LocationProps>>(
    route?.fields?.locations as Array<LocationProps>
  );

  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [userLocation, setUserLocation] = React.useState<Location | null>(null);
  const hideSearchBarOnLocationCount = parseInt(props?.params?.hideSearchBarOnLocationCount ?? '1');

  React.useEffect(() => {
    if (route?.fields?.locations) {
      setLocationsList(route.fields.locations as Array<LocationProps>);
    }
  }, [route?.fields?.locations]);

  const handleGetUserLocation = (isInputUpdated: boolean) => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const data = await getAddressFromServer(lat, lng);
        const currentLoc: Location = { address: data, lat, lng };

        if (data && isInputUpdated) {
          // Set value in LocationInput
          locationRef.current?.setValue?.(data);
        }

        // Trigger onLocationSelect logic
        setLocation(currentLoc);
        setUserLocation(currentLoc);
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Geolocation error: ' + error.message);
      },
      {
        timeout: 5000,
        maximumAge: 60000,
      }
    );
  };

  const handleCurrentLocationClick = () => {
    if (userLocation) {
      setLocation(userLocation);
      return;
    }
    handleGetUserLocation(true);
  };

  const handleSearchClick = async (LocationProps?: Location) => {
    let userLatLng = '';
    if (LocationProps) {
      setLocation(LocationProps);
      userLatLng = `${LocationProps?.lat},${LocationProps?.lng}`;
    } else {
      if (!location) return;
      userLatLng = `${location?.lat},${location?.lng}`;
    }

    // Only include locations with valid lat/lng
    const validLocations = locationsList.filter(
      (loc) =>
        typeof loc.fields?.Latitude?.value === 'number' &&
        typeof loc.fields?.Longitude?.value === 'number'
    );

    const destinations = validLocations
      .map((loc) => `${loc.fields?.Latitude?.value},${loc.fields?.Longitude?.value}`)
      .join('|');

    if (!destinations) return;

    // Batch call to Google Distance Matrix API via your server component
    const res = await fetch(
      `/api/get-distance?origins=${encodeURIComponent(userLatLng)}&destinations=${encodeURIComponent(destinations)}&units=imperial`
    );
    const data = await res.json();
    const elements = data?.rows?.[0]?.elements || [];

    // Map distances back to all locations
    const updatedLocations = locationsList.map((loc) => {
      const validIdx = validLocations.indexOf(loc);
      let miles: number | undefined = undefined;
      if (validIdx !== -1 && elements[validIdx]?.distance) {
        miles = elements[validIdx].distance.value / 1609.34;
      }
      return {
        ...loc,
        distance: miles,
        distanceDisplay: miles !== undefined ? `<b>${miles.toFixed(1)} miles</b>` : '',
      };
    });

    // Sort by distance (closest first)
    updatedLocations.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
    setLocationsList(updatedLocations);
  };

  const handleSortLocations = (locations: Array<LocationProps>) => {
    const safeLocations: Array<LocationProps> = Array.isArray(locations)
      ? locations
      : locations
        ? [locations]
        : [];
    // Default: sort all alphabetically
    return [...safeLocations].sort((a, b) => {
      const titleA = a?.fields?.Title?.value?.toLowerCase() || '';
      const titleB = b?.fields?.Title?.value?.toLowerCase() || '';
      return titleA.localeCompare(titleB);
    });
  };

  const handleLocationSelect = (selectedLocation: Location) => setLocation(selectedLocation);

  const handleOnClear = () => {
    setLocation(null);
    setLocationsList(route?.fields?.locations as Array<LocationProps>);
  };

  const handleHideShowLocations = () => {
    setIsCollapsed(!isCollapsed);
  };

  React.useEffect(() => {
    setLocationsList(handleSortLocations(locationsList));
  }, []);

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('showMoreLocation')) {
        //debugger;
        const el = document.querySelector('.location-listing');
        if (el) {
          const yOffset = -120;
          const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;

          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }
    }
  }, [router.query]);

  return (
    <ComponentWrapper isSidenavPart={true} className="location-listing">
      <div className="relative">
        {locationsList?.length > hideSearchBarOnLocationCount && (
          <>
            <span className="text-20 block leading-20 text-gray-900 font-bold">
              <PlainTextWrapper field={{ value: 'View locations closest to you' }} />
            </span>
            <div className="py-8">
              <ButtonWrapper
                ctaVariant="hyperlink"
                className="inline-flex !rounded-[1px] text-blue-base underline hover:cursor-pointer items-center gap-4 focus:outline-none"
                onClick={() => handleCurrentLocationClick()}
              >
                <SvgIcon icon="gpsfix" size="md" />
                <PlainTextWrapper field={{ value: 'Use my location' }} />
              </ButtonWrapper>
            </div>
          </>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
          {locationsList?.length > hideSearchBarOnLocationCount && (
            <div className="col-span-1 flex flex-row">
              <LocationInput
                ref={locationRef}
                onLocationSelect={handleLocationSelect}
                placeholder={{ value: 'Type a city or zip code' }}
                designType="mini"
                onClear={handleOnClear}
                disabledLiveLocation={true}
                onEnter={handleSearchClick}
              />
              <ButtonWrapper
                ctaVariant="primary"
                className="!p-0 min-w-[56px] rounded-l-[0px] rounded-r-sm text-blue-base underline flex items-center gap-4 z-10 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleSearchClick()}
                disabled={!location?.address}
                aria-label="Search locations near the entered location"
              >
                <SvgIcon icon="search" size="lg" />
              </ButtonWrapper>
            </div>
          )}
        </div>
        {locationsList?.length > 0 ? (
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 gap-10 ${
              locationsList?.length > hideSearchBarOnLocationCount ? 'mt-16' : ''
            }`}
            aria-live="polite"
          >
            {isCollapsed
              ? locationsList?.map((data: LocationProps, index: number) => (
                  <LocationCard key={index} {...data} userLocation={userLocation} />
                ))
              : locationsList
                  ?.slice(0, 4)
                  ?.map((data: LocationProps, index: number) => (
                    <LocationCard key={index} {...data} userLocation={userLocation} />
                  ))}
          </div>
        ) : (
          <div className="mt-16" aria-live="polite">
            <b>
              <PlainTextWrapper field={{ value: getDictionaryValue('NoLocationsFound') }} />
            </b>
          </div>
        )}
        {locationsList?.length > 4 && (
          <div className="mt-16">
            <ButtonWrapper
              ctaVariant="tertiary"
              className="inline-flex items-center px-7"
              onClick={() => handleHideShowLocations()}
              aria-expanded={isCollapsed}
              aria-controls="location-list"
            >
              {!isCollapsed ? (
                <>
                  <PlainTextWrapper
                    field={{
                      value: getDictionaryValue('ShowAllLocations').replace(
                        '#',
                        locationsList?.length.toString()
                      ),
                    }}
                    className="text-18 leading-18 mt-1"
                    tag="span"
                  />
                  <SvgIcon icon="plus" size="lg" />
                </>
              ) : (
                <>
                  <PlainTextWrapper
                    field={{ value: getDictionaryValue('ShowLess') }}
                    className="text-18 leading-18 mt-1"
                    tag="span"
                  />
                  <SvgIcon icon="minus" size="lg" />
                </>
              )}
            </ButtonWrapper>
          </div>
        )}
      </div>
    </ComponentWrapper>
  );
};

const LocationCard = (
  data: LocationProps & {
    userLocation?: Location | null;
  }
) => {
  const { getDictionaryValue } = useDictionary();
  const { locationDirectionsButton } = TAILWIND_VARIANTS();
  const siteSettings = useSiteSettings()?.fields;
  const { open } = useAppointmentModal();

  const setting =
    (siteSettings as {
      mapServiceUrl?: LinkField;
    }) ?? {};

  const FullAddress = getAddress({
    address: data?.fields?.address1?.value ?? '',
    city: data?.fields?.city?.value ?? '',
    state: data?.fields?.state?.fields?.name?.value ?? '',
    zip: data?.fields?.zip?.value ?? '',
  });

  const updatedLink = {
    value: {
      text: data?.fields?.Title?.value,
      title: data?.fields?.Title?.value,
      href: data?.url,
    },
  };

  const MapLocationUrl: string =
    data?.fields?.addressMapURL?.value?.href === ''
      ? `${setting?.mapServiceUrl?.value?.href}${encodeURIComponent(FullAddress)}`
      : '';

  const getDirectionLink = data?.fields?.addressMapURL?.value?.href
    ? {
        value: {
          ...data?.fields?.addressMapURL?.value,
          target: '_blank',
        },
      }
    : {
        value: {
          href: MapLocationUrl,
          title: getDictionaryValue('GetDirections'),
          text: getDictionaryValue('GetDirections'),
          target: '_blank',
        },
      };

  return (
    <div className="col-span-1 flex flex-col gap-8 rounded-md border border-gray-50 p-12 shadow-default hover:shadow-hover transition-all duration-300">
      <div className="flex flex-col gap-2">
        <span className="block font-normal text-16 text-gray-900">
          <PlainTextWrapper field={data?.fields?.PageType?.fields?.Value} />
        </span>
        <LinkWrapper
          className="w-full flex items-start gap-2 justify-between font-bold text-blue-base text-20 md:text-22 group hover:underline"
          field={updatedLink}
          aria-label={data?.fields?.Title?.value}
        >
          <PlainTextWrapper field={data?.fields?.Title} />
          <SvgIcon
            icon="arrow-right"
            size="lg"
            className="mt-2 opacity-0 group-hover:opacity-100 transition-all duration-300"
          />
        </LinkWrapper>
      </div>
      {FullAddress.trim() !== '' && getDirectionLink?.value?.href && (
        <div className="flex gap-4">
          <span className="inline-block text-orange-700">
            <SvgIcon icon={'map-pin'} size="lg" />
          </span>

          <div className="md:text-18 text-16">
            {FullAddress.trim() !== '' && (
              <>
                <div className="relative">
                  <RichTextWrapper
                    field={{
                      value: data?.distanceDisplay
                        ? `${data?.distanceDisplay} <span class="text-gray-500">(${getDictionaryValue('EstimatedDistance')})</span>`
                        : '',
                    }}
                    className="text-18 text-gray-900"
                  />
                </div>
              </>
            )}
            <LinkWrapper
              field={{
                value: {
                  href: getDirectionLink?.value?.href,
                },
              }}
              aria-label={`${getDictionaryValue('GetDirections')} to ${FullAddress}`}
            >
              <span className="text-18 group/address block">
                <span className="relative block text-gray-900 group-hover/address:underline underline-offset-[6px]">
                  <PlainTextWrapper
                    field={{
                      value: FullAddress,
                    }}
                  />
                </span>
                <span
                  className={classNames(
                    'inline-flex mt-4 md:mt-0 min-h-[48px] min-w-[48px] group-hover/address:underline underline-offset-[6px] text-gray-900',
                    locationDirectionsButton()
                  )}
                >
                  <PlainTextWrapper
                    field={{
                      value: getDirectionLink?.value?.text,
                    }}
                  />
                  <SvgIcon
                    icon="external-link"
                    className="!h-[22px] ml-4 min-w-[22px] !w-[22px] [&>path]:text-gray-900"
                    size="custom"
                    aria-label={`${getDictionaryValue('GetDirections')}`}
                  />
                </span>
              </span>
            </LinkWrapper>
          </div>
        </div>
      )}

      {data?.fields?.mobileVisiblePhoneNumber?.value && (
        <div className="flex gap-4">
          <span className="inline-block text-purple-700">
            <SvgIcon icon={'phone'} size="lg" />
          </span>
          <LinkWrapper
            className="text-18 text-gray-900 hover:underline underline-offset-[6px]"
            field={{
              value: {
                href: `tel:${data?.fields?.mobileVisiblePhoneNumber?.value?.replaceAll('-', '').trim()}`,
                text: `Call ${data?.fields?.mobileVisiblePhoneNumber?.value}`,
              },
            }}
          />
        </div>
      )}

      {data?.fields?.schedulingType?.name === 'Primary Care' && (
        <div className="flex gap-4">
          <ButtonWrapper
            text={getDictionaryValue('ScheduleAnAppointment')}
            ctaVariant="primary"
            className={`w-full md:w-[calc(50%-12px)] lg:w-full min-w-[280px]`}
            onClick={() =>
              open(
                data?.id ? (data?.id as string) : '',
                `?locationName=${data?.fields?.Title?.value}`,
                (data?.fields?.mobileVisiblePhoneNumber?.value as string)
                  ? (data?.fields?.mobileVisiblePhoneNumber?.value as string)
                  : ''
              )
            }
          />
        </div>
      )}
    </div>
  );
};

export const Default = withStandardComponentWrapper(LocationListing, false);

const TAILWIND_VARIANTS = tv({
  slots: {
    locationDirectionsButton: [
      'min-h-[48px]',
      'min-w-[48px]',
      'flex',
      'items-center',
      'justify-center',
      'rounded-md',
      'focus:outline-2',
      'focus:outline-blue-base',
      'transition',
      'duration-150',
    ],
  },
});
